import React, { FC, useEffect, useState } from 'react';
import {
  capitalize,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { CourierInvoiceLine, InvoiceShiftLine } from './hooks';
import './CourierInvoiceSummary.scss';
import clsx from 'clsx';
import { NO_REGION, NO_VEHICLE } from './utils';
import { roundToTwo } from '../../components/OrderListTable/utils/roundToTwo';

interface InvoiceLinesByRegion {
  [region: string]: CourierInvoiceLine[];
}

interface SummaryDetails {
  count: number;
  rate: number;
  total: number;
  summariesCount?: number;
}

interface IShiftsByVehicle {
  [vehicle: string]: InvoiceShiftLine[];
}

interface IShiftsByVehicleByRegion {
  [vehicle: string]: {
    [region: string]: SummaryDetails;
  };
}

interface SummaryData {
  vehicle: string;
  shifts: {
    [vehicle: string]: {
      [region: string]: SummaryDetails;
    };
  };
  deliveries: { [vehicle: string]: SummaryDetails };
  consolidated: SummaryDetails;
  rating: SummaryDetails;
  sorting: SummaryDetails;
  inbound: SummaryDetails;
  backup: SummaryDetails;
  extras: number;
  minComp: number;
  minCompThreshold?: string;
  idVerifications: SummaryDetails;
  extraWeekendPrice: number;
  deviations: number;
  km: SummaryDetails;
  total: number;
}

interface SummaryRow {
  classNames?: {
    row?: string;
    name?: string;
    vehicle?: string;
    region?: string;
    quantity?: string;
    rate?: string;
    total?: string;
  };
  name: string;
  vehicle?: string;
  invoicingRegion?: string;
  quantity?: string | number;
  rate?: string | number;
  total?: string | number;
}

const CourierInvoiceSummary: FC<{
  isVatRegistered: boolean;
  invoiceLines: CourierInvoiceLine[];
  courierRatings: { [region: string]: number };
}> = ({ isVatRegistered, invoiceLines, courierRatings }) => {
  const linesByRegion = {} as InvoiceLinesByRegion;
  invoiceLines.forEach(line => {
    if (linesByRegion[line.region] == null) {
      linesByRegion[line.region] = [];
    }
    linesByRegion[line.region].push(line);
  });

  return (
    <div className="courierInvoiceSummary">
      {Object.entries(linesByRegion).map(([region, lines]) => (
        <div key={region}>
          <div className="courierInvoiceSummary__top">
            <p>
              Invoice <b>{region}</b>
            </p>
          </div>
          <RegionSummaryTable
            isVatRegistered={isVatRegistered}
            invoiceLines={lines}
            courierRating={courierRatings[region]}
          />
        </div>
      ))}
    </div>
  );
};

const RegionSummaryTable: FC<{
  isVatRegistered: boolean;
  invoiceLines: CourierInvoiceLine[];
  courierRating: number;
}> = ({ isVatRegistered, invoiceLines, courierRating }) => {
  const [tableRows, setTableRows] = useState<SummaryRow[]>([]);

  useEffect(() => {
    const summaryData = {
      shifts: {},
      deliveries: {},
      consolidated: { count: 0, total: 0 },
      rating: { count: courierRating, total: 0 },
      sorting: { count: 0, total: 0 },
      inbound: { count: 0, total: 0 },
      backup: { count: 0, total: 0 },
      extras: 0,
      minComp: 0,
      extraWeekendPrice: 0,
      idVerifications: { count: 0, total: 0 },
      deviations: 0,
      km: { count: 0, total: 0 },
      total: 0,
    } as SummaryData;

    // Invoice lines are aggregated over multiple lines per day
    // Now we want all days within period
    invoiceLines.forEach(line => {
      const shiftsByVehicle = {} as IShiftsByVehicle;
      line.shifts.forEach(shift => {
        if (shiftsByVehicle[shift.vehicle] == null) {
          shiftsByVehicle[shift.vehicle] = [];
        }
        shiftsByVehicle[shift.vehicle].push(shift);
      });

      Object.entries(shiftsByVehicle).forEach(([vehicle, shifts]) => {
        if (summaryData.shifts[vehicle] == null) {
          summaryData.shifts[vehicle] = {};
        }
        shifts.forEach(shift => {
          if (summaryData.shifts[vehicle][shift.invoicingRegion] == null) {
            summaryData.shifts[vehicle][shift.invoicingRegion] = {
              count: 0,
              rate: 0,
              total: 0,
            };
          }
          // Assume same rating within each vehicle/region combination
          summaryData.shifts[vehicle][shift.invoicingRegion].rate = shift.price;
          summaryData.shifts[vehicle][shift.invoicingRegion].count +=
            shift.count;
          summaryData.shifts[vehicle][shift.invoicingRegion].total +=
            shift.price;
        });
      });

      Object.entries(line.deliveries).forEach(([vehicle, deliveries]) => {
        if (summaryData.deliveries[vehicle] == null) {
          summaryData.deliveries[vehicle] = {
            count: 0,
            rate: 0,
            total: 0,
            summariesCount: 0,
          };
        }
        // Due to compensation model changes, sum deliveries rates to calculate avg later
        summaryData.deliveries[vehicle].rate += deliveries.rate;
        summaryData.deliveries[vehicle].count += deliveries.count;
        summaryData.deliveries[vehicle].total += deliveries.total;
        summaryData.deliveries[vehicle].summariesCount += 1;
      });

      if (line.consolidated.rate) {
        summaryData.consolidated.rate = line.consolidated.rate;
      }
      summaryData.consolidated.count += line.consolidated.count;
      summaryData.consolidated.total += line.consolidated.total;

      // Overwriting existing threshold only happens when the value is the same.
      if (line.minComp.threshold) {
        summaryData.minCompThreshold = line.minComp.threshold;
      }
      summaryData.minComp += line.minComp.amount;

      summaryData.sorting.rate = line.sorting.rate;
      summaryData.sorting.count += line.sorting.count;
      summaryData.sorting.total += line.sorting.total;

      summaryData.inbound.rate = line.inbound.rate;
      summaryData.inbound.count += line.inbound.count;
      summaryData.inbound.total += line.inbound.total;

      summaryData.backup.rate = line.backup.rate;
      summaryData.backup.count += line.backup.count;
      summaryData.backup.total += line.backup.total;

      if (line.idVerifications.rate) {
        summaryData.idVerifications.rate = line.idVerifications.rate;
      }
      summaryData.idVerifications.count += line.idVerifications.count;
      summaryData.idVerifications.total += line.idVerifications.total;

      summaryData.extraWeekendPrice += line.extraWeekendPrice.total;
      summaryData.deviations += line.deviations.total;
      summaryData.extras += line.extras.amount;
      summaryData.km.count += line.km.count;
      if (line.km.rate) {
        summaryData.km.rate = line.km.rate;
      }
      summaryData.km.total += line.km.total;
      summaryData.total += line.total;
      summaryData.rating.total += line.bonus.total;
      summaryData.rating.rate = line.bonus.rate;
    });

    const shiftsRows = Object.entries(summaryData.shifts).flatMap(
      ([vehicle, shiftRegions]) =>
        Object.entries(shiftRegions).map(([region, shifts]) => ({
          name: 'Shifts',
          vehicle: vehicle === NO_VEHICLE ? '' : capitalize(vehicle),
          invoicingRegion: region === NO_REGION ? '' : region,
          quantity: shifts.count,
          rate: shifts.rate,
          total: shifts.total,
        }))
    );

    const deliveriesRows = Object.entries(summaryData.deliveries).map(
      ([vehicle, deliveries]) => ({
        name: 'Deliveries',
        vehicle: vehicle === NO_VEHICLE ? '' : capitalize(vehicle),
        quantity: deliveries.count,
        rate: (deliveries.rate / deliveries.summariesCount).toFixed(1),
        total: deliveries.total,
      })
    );

    // Due to different compensation models, hide bonus
    const firstRows: SummaryRow[] = [
      ...shiftsRows,
      ...deliveriesRows,
      {
        name: 'Consolidated',
        quantity: summaryData.consolidated.count,
        rate: summaryData.consolidated.rate,
        total: summaryData.consolidated.total,
      },
    ];

    if (summaryData.rating.total !== 0) {
      firstRows.push({
        classNames: {
          quantity:
            summaryData.rating.total > 0
              ? 'courierInvoiceSummaryTable__green'
              : '',
        },
        name: 'Rating/bonus',
        quantity: summaryData.rating.count,
        rate: summaryData.rating.rate,
        total: summaryData.rating.total,
      });
    }

    if (summaryData.idVerifications.total > 0) {
      firstRows.push({
        name: 'ID checks',
        quantity: summaryData.idVerifications.count,
        rate: summaryData.idVerifications.rate,
        total: summaryData.idVerifications.total,
      });
    }

    if (summaryData.extraWeekendPrice > 0) {
      firstRows.push({
        name: 'Weekend extra',
        total: summaryData.extraWeekendPrice,
      });
    }

    if (summaryData.deviations > 0) {
      firstRows.push({
        name: 'Deviations',
        total: summaryData.deviations,
      });
    }

    setTableRows([
      ...firstRows,
      {
        name: 'Price adj.',
        total: summaryData.minComp,
      },
      {
        name: 'Sorting',
        quantity: summaryData.sorting.count,
        total: roundToTwo(summaryData.sorting.total),
      },
      {
        name: 'Inbound',
        quantity: summaryData.inbound.count,
        total: summaryData.inbound.total,
      },
      {
        name: 'Backup',
        quantity: summaryData.backup.count,
        total: summaryData.backup.total,
      },
      {
        name: 'Other',
        total: summaryData.extras,
      },
      {
        name: 'KM',
        quantity: summaryData.km.count,
        rate: summaryData.km.rate,
        total: summaryData.km.total,
      },
      {
        classNames: {
          row: 'courierInvoiceSummaryTable__totalRows',
          total: 'courierInvoiceSummaryTable__bold',
        },
        name: 'Total (excl. VAT)',
        total: roundToTwo(summaryData.total),
      },
      {
        classNames: {
          row: 'courierInvoiceSummaryTable__totalRows',
          total: 'courierInvoiceSummaryTable__bold',
        },
        name: 'VAT (25%)',
        total: isVatRegistered ? roundToTwo(summaryData.total * 0.25) : 0,
      },
      {
        classNames: {
          row: 'courierInvoiceSummaryTable__totalRows',
          total: 'courierInvoiceSummaryTable__bold',
        },
        name: 'Total (incl. VAT)',
        total: isVatRegistered
          ? roundToTwo(summaryData.total * 1.25)
          : roundToTwo(summaryData.total),
      },
    ]);
  }, [isVatRegistered, invoiceLines, courierRating]);

  const alignLeft = 'courierInvoiceSummaryTable__alignLeft';
  const alignCenter = 'courierInvoiceSummaryTable__alignCenter';
  const alignRight = 'courierInvoiceSummaryTable__alignRight';
  return (
    <div className="courierInvoiceSummaryTable">
      <Table>
        <TableHead>
          <TableRow className="courierInvoiceSummaryTable__row">
            <TableCell>TYPE</TableCell>
            <TableCell className={alignCenter}>VEHICLE</TableCell>
            <TableCell className={alignLeft}>REGION</TableCell>
            <TableCell className={alignCenter}>COUNT</TableCell>
            <TableCell className={alignCenter}>RATE</TableCell>
            <TableCell className={alignRight}>AMOUNT</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row, i) => {
            const rowClass = clsx(
              'courierInvoiceSummaryTable__row',
              row.classNames?.row
            );
            const nameClass = clsx(alignLeft, row.classNames?.name);
            const vehicleClass = clsx(alignCenter, row.classNames?.vehicle);
            const regionClass = clsx(alignLeft, row.classNames?.region);
            const quantityClass = clsx(alignCenter, row.classNames?.quantity);
            const rateClass = clsx(alignCenter, row.classNames?.rate);
            const totalClass = clsx(alignRight, row.classNames?.total);
            return (
              <TableRow key={i} className={rowClass}>
                <TableCell className={nameClass}>{row.name}</TableCell>
                <TableCell className={vehicleClass}>
                  {row.vehicle || ''}
                </TableCell>
                <TableCell className={regionClass}>
                  {row.invoicingRegion || ''}
                </TableCell>
                <TableCell className={quantityClass}>
                  {row.quantity || ''}
                </TableCell>
                <TableCell className={rateClass}>{row.rate || ''}</TableCell>
                <TableCell className={totalClass}>{row.total || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default CourierInvoiceSummary;
